import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Chip, Divider, Drawer, Grid, IconButton, InputAdornment,
  LinearProgress, Paper, Tab, Tabs, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { DevicesAPI } from "../api/devices";
import { AssetsAPI } from "../api/assets";

const dash = "—";

function formatDate(value) {
  return value ? new Date(value).toLocaleString() : "Never";
}

function formatGb(bytes) {
  const value = Number(bytes);
  return Number.isFinite(value) && value > 0 ? `${(value / 1024 ** 3).toFixed(1)} GB` : dash;
}

function getDeviceStatus(device) {
  const contact = device?.last_heartbeat_at || device?.last_seen;
  if (!contact) return { label: "Unknown", color: "default" };
  const hours = (Date.now() - new Date(contact).getTime()) / 3600000;
  if (hours <= .5) return { label: "Online", color: "success" };
  if (hours <= 4) return { label: "Delayed", color: "warning" };
  if (hours <= 24) return { label: "Offline", color: "error" };
  return { label: "Stale", color: "default" };
}

function getStorage(device) {
  const entries = Array.isArray(device?.hw_meta?.filesystems) ? device.hw_meta.filesystems : [];
  const volumes = entries.filter((volume) => Number(volume?.size) > 0);
  const capacity = volumes.reduce((total, volume) => total + Number(volume.size || 0), 0);
  const used = volumes.reduce((total, volume) => total + Math.min(Number(volume.used || 0), Number(volume.size || 0)), 0);
  const percentage = capacity ? Math.round((used / capacity) * 100) : null;
  return { volumes, capacity, used, available: Math.max(0, capacity - used), percentage };
}

function Field({ label, value }) {
  return <Box><Typography variant="caption" color="text.secondary">{label}</Typography><Typography variant="body2" fontWeight={600}>{value || dash}</Typography></Box>;
}

function Section({ title, children }) {
  return <Paper variant="outlined" sx={{ p: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.75 }}>{title}</Typography>
    {children}
  </Paper>;
}

export default function DeviceDrawer({ open, onClose, device }) {
  const [tab, setTab] = useState(0);
  const [query, setQuery] = useState("");
  const [software, setSoftware] = useState({ loading: false, rows: [], error: "" });
  const [assets, setAssets] = useState({ loading: false, rows: [], error: "" });

  useEffect(() => {
    setTab(0); setQuery("");
    setSoftware({ loading: false, rows: [], error: "" });
    setAssets({ loading: false, rows: [], error: "" });
    if (!device) return;
    void (async () => {
      setSoftware({ loading: true, rows: [], error: "" });
      try {
        const response = await DevicesAPI.software(device.id, { limit: 1000 });
        setSoftware({ loading: false, rows: response?.data?.data || [], error: "" });
      } catch (error) {
        setSoftware({ loading: false, rows: [], error: error?.response?.data?.error || error.message });
      }
    })();
    void (async () => {
      setAssets({ loading: true, rows: [], error: "" });
      try {
        const response = await AssetsAPI.deviceAsset(device.id);
        setAssets({ loading: false, rows: response?.data?.data || [], error: "" });
      } catch (error) {
        setAssets({ loading: false, rows: [], error: error?.response?.data?.error || error.message });
      }
    })();
  }, [device]);

  const filteredSoftware = useMemo(() => {
    const term = query.trim().toLowerCase();
    return term ? software.rows.filter((item) => [item.name, item.vendor, item.version].some((value) => String(value || "").toLowerCase().includes(term))) : software.rows;
  }, [query, software.rows]);

  if (!device) return null;
  const hw = device.hw_meta || device.hardware || {};
  const os = device.os_info || {};
  const status = getDeviceStatus(device);
  const storage = getStorage(device);
  const linkedAsset = assets.rows[0] || null;
  const network = Array.isArray(hw.network) ? hw.network.find((item) => item.ip4) || hw.network[0] || {} : hw.network || {};
  const storageColor = storage.percentage >= 90 ? "error" : storage.percentage >= 80 ? "warning" : "primary";

  return <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        // The application header is fixed above drawers. Keep the device panel
        // below it so its title, status and close action are never obscured.
        top: { xs: 56, sm: 70 },
        height: { xs: "calc(100% - 56px)", sm: "calc(100% - 70px)" },
        width: { xs: "100%", sm: 620 },
        maxWidth: "100%",
      },
    }}
  >
    <Box sx={{ p: { xs: 2, sm: 3 }, minHeight: "100%", bgcolor: "background.default" }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
        <Box minWidth={0}>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <Typography variant="h6" fontWeight={800} noWrap>{device.hostname || device.device_uuid || "Managed device"}</Typography>
            <Chip size="small" label={status.label} color={status.color} />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>
            {os.distro || os.name || device.os_name || "Operating system not reported"}
          </Typography>
        </Box>
        <IconButton aria-label="Close device details" onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable" scrollButtons="auto" sx={{ mt: 2 }}>
        <Tab label="Overview" /><Tab label="Storage" /><Tab label="Hardware" />
        <Tab label={`Software${software.rows.length ? ` (${software.rows.length})` : ""}`} />
        <Tab label={`Assets${assets.rows.length ? ` (${assets.rows.length})` : ""}`} />
      </Tabs>
      <Divider sx={{ mb: 2 }} />

      {tab === 0 && <Box display="grid" gap={2}>
        <Section title="Status">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><Field label="Last agent check-in" value={formatDate(device.last_heartbeat_at || device.last_seen)} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Inventory last updated" value={formatDate(device.last_seen)} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Storage in use" value={storage.percentage == null ? dash : `${storage.percentage}% (${formatGb(storage.used)} of ${formatGb(storage.capacity)})`} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Linked asset" value={assets.loading ? "Loading" : linkedAsset?.asset_uid || "Not matched"} /></Grid>
          </Grid>
        </Section>
        <Section title="Assignment and device">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}><Field label="Asset custodian" value={linkedAsset?.asset_custodian || device.owner_name || device.assigned_to} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Asset owner" value={linkedAsset?.owner_name} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Serial number" value={device.serial} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Operating system" value={[os.distro || os.name, os.version || os.release].filter(Boolean).join(" ")} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Device identifier" value={device.device_uuid || device.id} /></Grid>
          </Grid>
        </Section>
      </Box>}

      {tab === 1 && <Box display="grid" gap={2}>
        <Section title="Storage summary">
          {storage.percentage == null ? <Typography variant="body2" color="text.secondary">No filesystem data has been received from this device yet.</Typography> : <><Grid container spacing={2}>
            <Grid item xs={4}><Field label="Capacity" value={formatGb(storage.capacity)} /></Grid>
            <Grid item xs={4}><Field label="Used" value={formatGb(storage.used)} /></Grid>
            <Grid item xs={4}><Field label="Free" value={formatGb(storage.available)} /></Grid>
          </Grid><Box sx={{ mt: 2 }}><Box display="flex" justifyContent="space-between" mb={.75}><Typography variant="body2" fontWeight={600}>Overall usage</Typography><Typography variant="body2" fontWeight={700}>{storage.percentage}%</Typography></Box><LinearProgress variant="determinate" value={storage.percentage} color={storageColor} sx={{ height: 7, borderRadius: 1 }} /></Box></>}
        </Section>
        {storage.volumes.length ? <Section title="Volumes"><Box display="grid" gap={1}>{storage.volumes.map((volume, index) => { const percent = Number(volume.use ?? (Number(volume.used || 0) / Number(volume.size || 1)) * 100); const volumeColor = percent >= 90 ? "error" : percent >= 80 ? "warning" : "primary"; return <Box key={`${volume.mount || volume.fs || "volume"}-${index}`} sx={{ py: 1, borderBottom: index < storage.volumes.length - 1 ? "1px solid" : 0, borderColor: "divider" }}><Box display="flex" justifyContent="space-between" gap={1}><Box><Typography variant="body2" fontWeight={600}>{volume.mount || volume.fs || `Volume ${index + 1}`}</Typography><Typography variant="caption" color="text.secondary">{formatGb(volume.used)} used of {formatGb(volume.size)}</Typography></Box><Typography variant="body2" fontWeight={700}>{Math.round(percent)}%</Typography></Box><LinearProgress variant="determinate" value={Math.min(100, Math.max(0, percent))} color={volumeColor} sx={{ mt: .75, height: 5, borderRadius: 1 }} /></Box>; })}</Box></Section> : null}
      </Box>}

      {tab === 2 && <Box display="grid" gap={2}>
        <Section title="Core hardware"><Grid container spacing={2}><Grid item xs={12} sm={6}><Field label="Processor" value={hw.cpu} /></Grid><Grid item xs={12} sm={6}><Field label="Memory" value={hw.ram_gb ? `${hw.ram_gb} GB` : null} /></Grid><Grid item xs={12} sm={6}><Field label="Manufacturer" value={hw.system?.manufacturer} /></Grid><Grid item xs={12} sm={6}><Field label="Model" value={hw.system?.model} /></Grid></Grid></Section>
        <Section title="Network"><Grid container spacing={2}><Grid item xs={12} sm={6}><Field label="IP address" value={network.ip4 || network.ip_address || device.ip_address} /></Grid><Grid item xs={12} sm={6}><Field label="MAC address" value={network.mac || network.mac_address || hw.uuid?.macs?.[0]} /></Grid></Grid></Section>
      </Box>}

      {tab === 3 && <Section title="Installed software"><Box display="flex" justifyContent="space-between" alignItems="center" gap={1} flexWrap="wrap" mb={1.5}><Typography variant="body2" color="text.secondary">{software.loading ? "Loading applications…" : `${software.rows.length} applications reported`}</Typography><TextField size="small" placeholder="Search software" value={query} onChange={(event) => setQuery(event.target.value)} InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} /></Box>
        {software.error ? <Typography color="error">{software.error}</Typography> : filteredSoftware.length ? <Box sx={{ maxHeight: 440, overflow: "auto" }}>{filteredSoftware.map((item, index) => <Box key={`${item.name}-${item.version}-${index}`} sx={{ py: 1, borderBottom: "1px solid", borderColor: "divider" }}><Typography variant="body2" fontWeight={600}>{item.name || "Unnamed application"}</Typography><Typography variant="caption" color="text.secondary">{[item.vendor, item.version].filter(Boolean).join(" · ") || "Publisher and version not reported"}</Typography></Box>)}</Box> : !software.loading ? <Typography variant="body2" color="text.secondary">No software has been reported.</Typography> : null}
      </Section>}

      {tab === 4 && <Section title="Linked assets"><Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>A linked asset connects this scanned device with its purchase and ownership record.</Typography>
        {assets.loading ? <Typography variant="body2" color="text.secondary">Loading assets…</Typography> : assets.error ? <Typography color="error">{assets.error}</Typography> : assets.rows.length ? <Box display="grid" gap={1}>{assets.rows.map((asset) => <Box key={asset.id} sx={{ p: 1.25, border: "1px solid", borderColor: "divider", borderRadius: 1.5 }}><Typography variant="body2" fontWeight={600}>{asset.asset_uid || "Asset"}</Typography><Typography variant="caption" color="text.secondary">{asset.asset_type || "Asset type not reported"}</Typography><Typography variant="body2" sx={{ mt: 0.75 }}><strong>Custodian:</strong> {asset.asset_custodian || "Not set"}</Typography><Typography variant="body2"><strong>Owner:</strong> {asset.owner_name || "Not set"}</Typography></Box>)}</Box> : <Typography variant="body2" color="text.secondary">No asset is linked to this scanned device.</Typography>}
      </Section>}
    </Box>
  </Drawer>;
}
